import type { HermesMessage } from "@/lib/hermes-api";

/**
 * 行内引用注入用的 href 前缀。
 * 必须用 `#…` 片段：自定义 scheme（如 `hermes-cite:`）会被 rehype-sanitize 剥掉 href，
 * 再被 rehype-harden 标成用户可见的 ` [blocked]`。
 */
export const HERMES_CITE_HREF_PREFIX = "#hermes-cite:";

export type HermesCitationSource = {
  number: string;
  kind: "web" | "kb" | string;
  title: string;
  url: string;
  /** KB：Wiki 展示名；Web：摘要/体积等次要信息 */
  description?: string | null;
  bytes?: number | null;
  truncated?: boolean;
};

/** 内部知识库页面深链（来源列表 / 行内引用）。 */
export type KbCitationPageRef = {
  accountId: string;
  brandId?: string;
  wikiId: number;
  pageId: number;
  /** `#seed-field-<key>` 时的字段键 */
  seedField?: string;
};

/** 是否应弹窗预览（KB 或站内相对路径），而非整页替换当前聊天视图。 */
export function isInternalCitationSource(source: HermesCitationSource): boolean {
  return source.kind === "kb" || source.url.startsWith("/");
}

/**
 * 解析 `/accounts/:id/kb/wikis/:w/pages/:p` 或品牌栈
 * `/accounts/:id/brands/:b/kb/wikis/:w/pages/:p`（可带 `#seed-field-…`）。
 */
export function parseKbCitationPageUrl(url: string): KbCitationPageRef | null {
  const raw = url.trim();
  if (!raw.startsWith("/")) return null;

  let pathname = raw;
  let hash = "";
  const hashIndex = raw.indexOf("#");
  if (hashIndex >= 0) {
    pathname = raw.slice(0, hashIndex);
    hash = raw.slice(hashIndex + 1);
  }

  const brandMatch = pathname.match(
    /^\/accounts\/([^/]+)\/brands\/([^/]+)\/kb\/wikis\/(\d+)\/pages\/(\d+)\/?$/,
  );
  if (brandMatch) {
    return {
      accountId: brandMatch[1]!,
      brandId: brandMatch[2]!,
      wikiId: Number(brandMatch[3]),
      pageId: Number(brandMatch[4]),
      seedField: seedFieldFromHash(hash),
    };
  }

  const enterpriseMatch = pathname.match(
    /^\/accounts\/([^/]+)\/kb\/wikis\/(\d+)\/pages\/(\d+)\/?$/,
  );
  if (enterpriseMatch) {
    return {
      accountId: enterpriseMatch[1]!,
      wikiId: Number(enterpriseMatch[2]),
      pageId: Number(enterpriseMatch[3]),
      seedField: seedFieldFromHash(hash),
    };
  }

  return null;
}

function seedFieldFromHash(hash: string): string | undefined {
  const match = hash.match(/^seed-field-([a-zA-Z0-9_]+)$/);
  return match?.[1];
}

export function parseHermesSources(
  toolPayload: HermesMessage["tool_payload"] | null | undefined,
): HermesCitationSource[] {
  if (!toolPayload || typeof toolPayload !== "object") return [];
  const raw = toolPayload.sources;
  if (!Array.isArray(raw)) return [];

  const parsed = raw.flatMap((item, index) => {
    if (!item || typeof item !== "object") return [];
    const record = item as Record<string, unknown>;
    const url = typeof record.url === "string" ? record.url.trim() : "";
    if (!url) return [];
    const number =
      typeof record.number === "string" && record.number.trim()
        ? record.number.trim()
        : String(index + 1);
    const title =
      typeof record.title === "string" && record.title.trim()
        ? record.title.trim()
        : url;
    const description =
      typeof record.description === "string" ? record.description : null;
    return [
      {
        number,
        kind: typeof record.kind === "string" ? record.kind : "web",
        title,
        url,
        // 体积文案勿进 description（历史 webfetch 数据）
        description: isByteSizeLabel(description) ? null : description,
        bytes: typeof record.bytes === "number" ? record.bytes : null,
        truncated: record.truncated === true,
      },
    ];
  });

  return dedupeSourcesByUrl(parsed);
}

/** 同 URL 合并：保留更早编号与更可读标题（兼容历史 websearch+webfetch 重复行） */
function dedupeSourcesByUrl(
  sources: HermesCitationSource[],
): HermesCitationSource[] {
  const out: HermesCitationSource[] = [];
  const indexByKey = new Map<string, number>();

  for (const source of sources) {
    const key = normalizeSourceUrl(source.url);
    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, out.length);
      out.push(source);
      continue;
    }

    const existing = out[existingIndex]!;
    if (preferSourceTitle(source.title, existing.title, existing.url)) {
      existing.title = source.title;
    }
    if (source.description && !existing.description) {
      existing.description = source.description;
    }
    if (source.bytes != null) existing.bytes = source.bytes;
    if (source.truncated) existing.truncated = true;
  }

  return out;
}

function normalizeSourceUrl(url: string): string {
  const raw = url.trim();
  if (!raw || raw.startsWith("/")) return raw;
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const path = parsed.pathname.replace(/\/$/, "");
    return `${parsed.protocol}//${host}${path}`;
  } catch {
    return raw.toLowerCase();
  }
}

function preferSourceTitle(
  candidate: string,
  current: string,
  url: string,
): boolean {
  const cand = candidate.trim();
  const cur = current.trim();
  if (!cand) return false;
  if (!cur || cur === url) return true;
  return isHostnameLikeTitle(cur, url) && !isHostnameLikeTitle(cand, url);
}

function isHostnameLikeTitle(title: string, url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    const t = title.trim().toLowerCase();
    return t === host || t === host.replace(/^www\./, "");
  } catch {
    return false;
  }
}

function isByteSizeLabel(value: string | null): boolean {
  if (!value) return false;
  return /^\d+(\.\d+)?\s*(B|KB|MB|GB)\b/i.test(value.trim());
}

export function hasInlineCitationMarkers(content: string): boolean {
  return /\[\d+\]/.test(content);
}

export function findSourceByNumber(
  sources: HermesCitationSource[],
  number: string,
): HermesCitationSource | undefined {
  return sources.find((source) => source.number === number);
}

/**
 * 将裸 `[n]`（非已有 Markdown 链接）改写为 `#hermes-cite:n` 链接，
 * 便于整篇一次过 Streamdown，避免拆段 + `p { inline }` 挤坏标题/列表。
 */
export function rewriteCitationMarkersForMarkdown(content: string): string {
  return content.replace(/\[(\d+)\](?!\()/g, (_match, number: string) => {
    return `[${number}](${HERMES_CITE_HREF_PREFIX}${number})`;
  });
}

export function parseHermesCiteHref(
  href: string | null | undefined,
): string | null {
  if (!href || !href.startsWith(HERMES_CITE_HREF_PREFIX)) return null;
  const number = href.slice(HERMES_CITE_HREF_PREFIX.length).trim();
  return /^\d+$/.test(number) ? number : null;
}

/** 行内引用徽章文案：KB 优先 Wiki 名，其次页面标题；避免一律「知识库」。 */
export function citationBadgeLabel(source: HermesCitationSource): string {
  const site = source.description?.trim();
  const title = source.title?.trim();

  if (source.kind === "kb") {
    const label = site || title;
    if (label) return truncateLabel(label, 18);
    return "知识库";
  }

  if (title && title !== source.url) return truncateLabel(title, 18);

  try {
    if (source.url.startsWith("http")) {
      return new URL(source.url).hostname || "来源";
    }
  } catch {
    /* fall through */
  }
  return "来源";
}

export function citationSourceListTitle(source: HermesCitationSource): string {
  const title = source.title?.trim() || source.url;
  // Web：只展示页面标题；体积/摘要不进主文案
  if (source.kind !== "kb") return title;

  const site = source.description?.trim();
  if (site && site !== title) return `${title} · ${site}`;
  return title;
}

function truncateLabel(label: string, max: number): string {
  const chars = Array.from(label);
  if (chars.length <= max) return label;
  return `${chars.slice(0, max - 1).join("")}…`;
}
